import { useMemo } from 'react';
import { Badge } from '@xiltrion/ui/badge';
import { Plegable } from '@xiltrion/ui/bloques/plegable';
import { Timeline, TimelineContent, TimelineHeader, TimelineIndicator, TimelineItem, TimelineSeparator, TimelineTitle } from '@xiltrion/ui/timeline';
import { Icono } from '@xiltrion/ui/icono';
import { RichTextView } from '@/components/RichTextEditor';
import { fmtDate } from '@/components/ui';
import { ResponseAttachmentsView } from './ResponseAttachments';
import { wordDiff } from './responseDiff';
import type { TaskDetail, TaskResponse } from './api';

const STATUS: Record<string, { label: string; variant: 'warning-light' | 'info-light' | 'success-light' | 'primary-light' }> = {
  REJECTED: { label: 'Devuelta', variant: 'warning-light' },
  SUBMITTED: { label: 'En revisión', variant: 'info-light' },
  APPROVED: { label: 'Aprobada', variant: 'primary-light' },
  FINAL: { label: 'Definitiva', variant: 'success-light' },
};

/** Texto plano de la respuesta conservando los párrafos, para comparar versiones. */
const plain = (body: string) => body.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|h[1-6]|li|tr)>/gi, '\n').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/\n{3,}/g, '\n\n').trim();

/** Qué cambió de una versión a la siguiente: lo agregado resaltado, lo quitado tachado. */
function Changes({ before, after }: { before: string; after: string }) {
  const parts = useMemo(() => wordDiff(plain(before), plain(after)), [before, after]);
  if (!parts) return <p className="m-0 text-muted-foreground">La respuesta es demasiado larga para comparar las versiones aquí.</p>;
  if (!parts.some((p) => p.kind !== 'same')) return <p className="m-0 text-muted-foreground">El texto no cambió.</p>;
  return (
    <p className="m-0 whitespace-pre-wrap text-sm bg-muted rounded p-2 max-h-80 overflow-auto">
      {parts.map((p, i) => p.kind === 'same' ? <span key={i}>{p.text}</span>
        : p.kind === 'added' ? <ins key={i} className="no-underline bg-success/15 text-success rounded-sm">{p.text}</ins>
        : <del key={i} className="bg-destructive/10 text-destructive rounded-sm">{p.text}</del>)}
    </p>
  );
}

/** La última versión de la respuesta, si la devolvieron: con ella arranca el borrador de la nueva tarea «Responder». */
export function returnedResponse(task: TaskDetail) {
  const last = task.actionCode === 'RESPOND' ? task.responses[0] : undefined;
  return last?.status === 'REJECTED' ? last : undefined;
}

/** Hay historial cuando hubo más de un envío o alguno se devolvió. */
export const hasResponseHistory = (responses: TaskResponse[]) => responses.length > 1 || responses.some((r) => r.status === 'REJECTED');

/**
 * Historial de la respuesta del caso: cada versión que se envió a revisión, la más reciente arriba, con quién la escribió,
 * quién la devolvió (en qué paso y por qué) y qué cambió frente a la anterior. Con una sola versión no hay historial.
 */
export function ResponseHistory({ caseId, responses, title = 'Historial de la respuesta' }: { caseId: string; responses: TaskResponse[]; title?: string }) {
  const versions = [...responses].sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
  if (!hasResponseHistory(versions)) return null;
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <div className="text-sm font-medium">{title} <span className="text-muted-foreground font-normal tnum">({versions.length} {versions.length === 1 ? 'versión' : 'versiones'})</span></div>
      <Timeline orientation="vertical" role="list" className="ps-1">
        {versions.map((r, k) => {
          const previous = versions[k + 1];
          const st = STATUS[r.status ?? ''] ?? { label: r.status ?? '', variant: 'info-light' as const };
          return (
            <TimelineItem key={r.id} step={k + 1} role="listitem" className="group-data-[orientation=vertical]/timeline:ms-7 group-data-[orientation=vertical]/timeline:not-last:pb-4">
              <TimelineHeader className="flex items-center gap-2 flex-wrap">
                <TimelineSeparator className="bg-border! group-data-[orientation=vertical]/timeline:-left-5 group-data-[orientation=vertical]/timeline:h-[calc(100%-1.25rem)] group-data-[orientation=vertical]/timeline:translate-y-5" />
                <TimelineTitle className="text-sm">Versión <span className="tnum">{r.version ?? k + 1}</span></TimelineTitle>
                <Badge variant={st.variant} size="sm">{st.label}</Badge>
                <TimelineIndicator className="flex size-5 items-center justify-center border-2 group-data-[orientation=vertical]/timeline:-left-5 group-data-[orientation=vertical]/timeline:top-0 bg-background">
                  <Icono nombre={r.status === 'REJECTED' ? 'mdi:undo-variant' : r.status === 'FINAL' ? 'mdi:check' : 'mdi:file-document-edit-outline'} className="size-3" />
                </TimelineIndicator>
              </TimelineHeader>
              <TimelineContent className="mt-1 flex flex-col gap-1.5 text-sm">
                <div className="text-muted-foreground">Enviada{r.authorName ? ` por ${r.authorName}` : ''}{r.createdAt ? ` el ${fmtDate(r.createdAt)}` : ''}</div>
                {r.status === 'REJECTED' && (
                  <div className="border border-warning/40 bg-warning/10 rounded p-2">
                    <div>Devuelta{r.rejectedInStep ? ` en «${r.rejectedInStep}»` : ''}{r.rejectedByName ? ` por ${r.rejectedByName}` : ''}{r.rejectedAt ? ` el ${fmtDate(r.rejectedAt)}` : ''}</div>
                    <div className="whitespace-pre-wrap text-foreground">{r.rejectionReason ? `Motivo: ${r.rejectionReason}` : <span className="text-muted-foreground">Sin motivo escrito.</span>}</div>
                  </div>
                )}
                <Plegable titulo="Ver el texto"><RichTextView value={r.body ?? ''} /></Plegable>
                {previous && <Plegable titulo={`Ver los cambios frente a la versión ${previous.version ?? k + 2}`}><Changes before={previous.body ?? ''} after={r.body ?? ''} /></Plegable>}
                <ResponseAttachmentsView caseId={caseId} ids={r.attachmentDocumentIds} />
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </section>
  );
}
